import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Paper,
  Tab,
  Tabs,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Breadcrumbs,
  Link,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  Security,
  Notifications,
  History,
  Edit,
  Add,
  Delete,
  Save,
  Cancel,
  Home,
  Business,
  Verified,
  ShoppingBag,
  Payment,
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import {
  updateUserProfile,
  deleteUserAccount,
  sendDeletionOtp,
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  resetPasswordWithOtp,
  clearError,
} from '../store/slices/authSlice';
import {
  fetchCustomerProfile,
  fetchCustomerOrders,
  fetchCustomerAddresses,
  addCustomerAddress,
  updateCustomerAddress,
  deleteCustomerAddress,
  clearCustomerError
} from '../store/customerSlice';

const ProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const {
    orders,
    profile,
    addresses: customerAddresses,
    loading: customerLoading,
    error: customerError
  } = useSelector((state) => state.customer);
  
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deletionOtp, setDeletionOtp] = useState('');
  const [deletionOtpError, setDeletionOtpError] = useState('');
  const [deletionOtpSending, setDeletionOtpSending] = useState(false);
  const [deletionResendCooldown, setDeletionResendCooldown] = useState(0);

  // Change-password dialog state
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [changePwdStep, setChangePwdStep] = useState(0);
  const [changePwdEmail, setChangePwdEmail] = useState('');
  const [changePwdEmailError, setChangePwdEmailError] = useState('');
  const [changePwdOtp, setChangePwdOtp] = useState('');
  const [changePwdOtpError, setChangePwdOtpError] = useState('');
  const [changePwdNew, setChangePwdNew] = useState('');
  const [changePwdConfirm, setChangePwdConfirm] = useState('');
  const [changePwdNewError, setChangePwdNewError] = useState('');
  const [changePwdConfirmError, setChangePwdConfirmError] = useState('');
  const [changePwdShowNew, setChangePwdShowNew] = useState(false);
  const [changePwdShowConfirm, setChangePwdShowConfirm] = useState(false);
  const [changePwdCooldown, setChangePwdCooldown] = useState(0);
  const [changePwdDone, setChangePwdDone] = useState(false);
  const [changePwdLoading, setChangePwdLoading] = useState(false);
  const changePwdTimerRef = useRef(null);
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
  });
  
  // Fetch customer data on component mount
  useEffect(() => {
    console.log('ProfilePage mounted');
    console.log('localStorage user:', localStorage.getItem('user'));
    console.log('localStorage token:', localStorage.getItem('token'));
    console.log('Redux user:', user);
    
    if (user) {
      console.log('User data available, populating profile...');
      if (user.id) {
        dispatch(fetchCustomerAddresses(user.id));
        dispatch(fetchCustomerOrders({ customerId: user.id }));
      }
    } else {
      console.log('No user data found');
    }
  }, [dispatch, user]);
  
  // Update profile data when profile is loaded (only when not in edit mode to prevent cursor jumping)
  useEffect(() => {
    // Don't update profile data when user is editing to prevent cursor jumping
    if (editMode) return;
    
    console.log('ProfilePage - User data:', user);
    console.log('ProfilePage - Profile data:', profile);
    
    if (profile) {
      console.log('Using profile data');
      setProfileData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        address: typeof profile.address === 'object' && profile.address !== null ? 
          `${profile.address.street || ''}, ${profile.address.city || ''}, ${profile.address.state || ''}, ${profile.address.country || ''} ${profile.address.postalCode || ''}`.trim() : 
          profile.address || '',
      });
    } else if (user) {
      console.log('Using user data');
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        address: typeof user.address === 'object' && user.address !== null ? 
          `${user.address.street || ''}, ${user.address.city || ''}, ${user.address.state || ''}, ${user.address.country || ''} ${user.address.postalCode || ''}`.trim() : 
          user.address || '',
      });
    }
  }, [profile, user, editMode]);
  
  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearCustomerError());
      if (changePwdTimerRef.current) clearInterval(changePwdTimerRef.current);
    };
  }, [dispatch]);

  // Send deletion OTP when dialog opens; start resend cooldown
  useEffect(() => {
    if (!deleteDialogOpen) return;
    const sendOtp = async () => {
      setDeletionOtpSending(true);
      setDeletionOtpError('');
      try {
        await dispatch(sendDeletionOtp()).unwrap();
        setDeletionResendCooldown(60);
      } catch (err) {
        setDeletionOtpError(err || 'Failed to send OTP. Please try again.');
      } finally {
        setDeletionOtpSending(false);
      }
    };
    sendOtp();
  }, [deleteDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer for deletion OTP resend
  useEffect(() => {
    if (deletionResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setDeletionResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [deletionResendCooldown]);

  // Change-password helpers
  const openChangePwdDialog = () => {
    setChangePwdStep(0);
    setChangePwdEmail(user?.email || '');
    setChangePwdEmailError('');
    setChangePwdOtp('');
    setChangePwdOtpError('');
    setChangePwdNew('');
    setChangePwdConfirm('');
    setChangePwdNewError('');
    setChangePwdConfirmError('');
    setChangePwdCooldown(0);
    setChangePwdDone(false);
    dispatch(clearError());
    setChangePwdOpen(true);
  };

  const closeChangePwdDialog = () => {
    if (changePwdTimerRef.current) clearInterval(changePwdTimerRef.current);
    setChangePwdOpen(false);
  };

  const startChangePwdCooldown = () => {
    setChangePwdCooldown(60);
    changePwdTimerRef.current = setInterval(() => {
      setChangePwdCooldown((prev) => {
        if (prev <= 1) { clearInterval(changePwdTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChangePwdSendOtp = async () => {
    if (!changePwdEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(changePwdEmail)) {
      setChangePwdEmailError('Please enter a valid email address');
      return;
    }
    setChangePwdEmailError('');
    setChangePwdLoading(true);
    try {
      await dispatch(sendForgotPasswordOtp(changePwdEmail)).unwrap();
      toast.success('OTP sent to your email address');
      startChangePwdCooldown();
      setChangePwdStep(1);
    } catch (e) {
      toast.error(e?.message || 'Failed to send OTP');
    } finally {
      setChangePwdLoading(false);
    }
  };

  const handleChangePwdResendOtp = async () => {
    if (changePwdCooldown > 0) return;
    setChangePwdLoading(true);
    try {
      await dispatch(sendForgotPasswordOtp(changePwdEmail)).unwrap();
      toast.success('OTP resent to your email address');
      startChangePwdCooldown();
      setChangePwdOtp('');
      setChangePwdOtpError('');
    } catch (e) {
      toast.error(e?.message || 'Failed to resend OTP');
    } finally {
      setChangePwdLoading(false);
    }
  };

  const handleChangePwdVerifyOtp = async () => {
    if (!changePwdOtp || changePwdOtp.length !== 6) {
      setChangePwdOtpError('Please enter the 6-digit OTP');
      return;
    }
    setChangePwdOtpError('');
    setChangePwdLoading(true);
    try {
      await dispatch(verifyForgotPasswordOtp({ email: changePwdEmail, otp: changePwdOtp })).unwrap();
      toast.success('OTP verified successfully');
      setChangePwdStep(2);
    } catch (e) {
      toast.error(e?.message || 'Invalid or expired OTP');
    } finally {
      setChangePwdLoading(false);
    }
  };

  const handleChangePwdReset = async () => {
    let hasError = false;
    if (!changePwdNew || changePwdNew.length < 8) {
      setChangePwdNewError('Password must be at least 8 characters');
      hasError = true;
    } else if (!/[A-Z]/.test(changePwdNew) || !/[0-9]/.test(changePwdNew)) {
      setChangePwdNewError('Password must include at least one uppercase letter and one number');
      hasError = true;
    } else {
      setChangePwdNewError('');
    }
    if (changePwdNew !== changePwdConfirm) {
      setChangePwdConfirmError('Passwords do not match');
      hasError = true;
    } else {
      setChangePwdConfirmError('');
    }
    if (hasError) return;

    setChangePwdLoading(true);
    try {
      await dispatch(resetPasswordWithOtp({ email: changePwdEmail, otp: changePwdOtp, newPassword: changePwdNew })).unwrap();
      setChangePwdDone(true);
      toast.success('Password changed successfully!');
    } catch (e) {
      toast.error(e?.message || 'Failed to reset password');
    } finally {
      setChangePwdLoading(false);
    }
  };

  const [newAddress, setNewAddress] = useState({
    type: 'home',
    name: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    isDefault: false,
  });

  const [addressErrors, setAddressErrors] = useState({});
  
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: true,
    marketingEmails: false,
    orderUpdates: true,
    newsletter: false,
  });

  useEffect(() => {
    // Don't update profile data when user is editing to prevent cursor jumping
    if (editMode) {
      // Still fetch orders even in edit mode
      if (user?.id) {
        dispatch(fetchCustomerOrders({ customerId: user.id, page: 0, size: 10 }));
      }
      return;
    }
    
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        dateOfBirth: user.dateOfBirth || '',
        gender: user.gender || '',
      });
      
      // Fetch user orders
      dispatch(fetchCustomerOrders({ customerId: user.id, page: 0, size: 10 }));
    }
  }, [user, dispatch, editMode]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleProfileUpdate = async () => {
    if (!user?.id) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    try {
      // Only send editable fields to the backend
      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phoneNumber: profileData.phoneNumber,
        address: profileData.address,
      };
      
      const updatedUser = await dispatch(updateUserProfile({ userId: user.id, userData: updateData })).unwrap();
      
      // Update local state with updated user data
      if (updatedUser) {
        setProfileData({
          firstName: updatedUser.firstName || profileData.firstName,
          lastName: updatedUser.lastName || profileData.lastName,
          email: updatedUser.email || profileData.email,
          phoneNumber: updatedUser.phoneNumber || profileData.phoneNumber,
          address: typeof updatedUser.address === 'object' && updatedUser.address !== null ? 
            `${updatedUser.address.street || ''}, ${updatedUser.address.city || ''}, ${updatedUser.address.state || ''}, ${updatedUser.address.country || ''} ${updatedUser.address.postalCode || ''}`.trim() : 
            updatedUser.address || profileData.address,
        });
      }
      
      setEditMode(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error || 'Failed to update profile. Please try again.');
    }
  };

  const handleConfirmDeleteAccount = async () => {
    if (!deletionOtp.trim()) {
      setDeletionOtpError('Please enter the OTP.');
      return;
    }
    setDeletingAccount(true);
    setDeletionOtpError('');
    try {
      await dispatch(deleteUserAccount(deletionOtp.trim())).unwrap();
      toast.success('Your account has been deleted successfully.');
      navigate('/');
    } catch (error) {
      setDeletionOtpError(error || 'Failed to delete account. Please try again.');
      setDeletingAccount(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deletingAccount) return;
    setDeleteDialogOpen(false);
    setDeletionOtp('');
    setDeletionOtpError('');
    setDeletionResendCooldown(0);
  };

  const handleResendDeletionOtp = async () => {
    setDeletionOtpSending(true);
    setDeletionOtpError('');
    try {
      await dispatch(sendDeletionOtp()).unwrap();
      setDeletionResendCooldown(60);
      toast.success('OTP resent to your email.');
    } catch (err) {
      setDeletionOtpError(err || 'Failed to resend OTP. Please try again.');
    } finally {
      setDeletionOtpSending(false);
    }
  };

  const validateAddressForm = () => {
    const errors = {};
    const errorMessages = [];
    
    if (!newAddress.name.trim()) {
      errors.name = 'Address name is required';
      errorMessages.push('Address name is required');
    }
    
    if (!newAddress.street.trim()) {
      errors.street = 'Street address is required';
      errorMessages.push('Street address is required');
    }
    
    if (!newAddress.city.trim()) {
      errors.city = 'City is required';
      errorMessages.push('City is required');
    }
    
    if (!newAddress.state.trim()) {
      errors.state = 'State is required';
      errorMessages.push('State is required');
    }
    
    if (!newAddress.pincode.trim()) {
      errors.pincode = 'Pincode is required';
      errorMessages.push('Pincode is required');
    } else if (!/^\d{6}$/.test(newAddress.pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
      errorMessages.push('Pincode must be 6 digits');
    }
    
    if (!newAddress.country.trim()) {
      errors.country = 'Country is required';
      errorMessages.push('Country is required');
    }
    
    setAddressErrors(errors);
    return errorMessages;
  };

  const handleAddressFieldChange = (field, value) => {
    setNewAddress({ ...newAddress, [field]: value });
    
    // Clear error for this field when user starts typing
    if (addressErrors[field]) {
      setAddressErrors({ ...addressErrors, [field]: '' });
    }
  };

  const handleDialogClose = () => {
    setAddressDialogOpen(false);
    setEditingAddress(null);
    setAddressErrors({});
    setNewAddress({
      type: 'home',
      name: '',
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      landmark: '',
      isDefault: false,
    });
  };

  const handleAddressSubmit = async () => {
    // Validate form before submission
    const validationErrors = validateAddressForm();
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]); // Show first error
      return;
    }

    try {
      // Map frontend fields to backend expected fields
      const addressData = {
        addressName: newAddress.name,
        addressType: newAddress.type.toUpperCase(),
        street: newAddress.street,
        city: newAddress.city,
        state: newAddress.state,
        country: newAddress.country,
        postalCode: newAddress.pincode,
        landmark: newAddress.landmark || '',
        default: newAddress.isDefault
      };

      if (editingAddress) {
        await dispatch(updateCustomerAddress({
          customerId: user.id,
          addressId: editingAddress.id,
          addressData: addressData
        })).unwrap();
        toast.success('Address updated successfully!');
      } else {
        await dispatch(addCustomerAddress({
          customerId: user.id,
          addressData: addressData
        })).unwrap();
        toast.success('Address added successfully!');
      }
      setAddressDialogOpen(false);
      setEditingAddress(null);
      setAddressErrors({});
      setNewAddress({
        type: 'home',
        name: '',
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        landmark: '',
        isDefault: false,
      });
      // Refresh addresses list
      dispatch(fetchCustomerAddresses(user.id));
    } catch (error) {
      toast.error(error || 'Failed to save address');
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    // Map backend fields to frontend form fields
    setNewAddress({
      type: address.addressType?.toLowerCase() || 'home',
      name: address.addressName || '',
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.postalCode || '',
      country: address.country || 'India',
      landmark: address.landmark || '',
      isDefault: address.default || false,
    });
    setAddressDialogOpen(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await dispatch(deleteCustomerAddress({
          customerId: user.id,
          addressId: addressId
        })).unwrap();
        toast.success('Address deleted successfully!');
      } catch (error) {
        toast.error(error || 'Failed to delete address');
      }
    }
  };

  const handlePreferenceChange = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">My Profile</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #2C3E50 0%, #2C3E50 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
          <Avatar
            sx={{ width: 80, height: 80, mr: 3, bgcolor: 'rgba(255,255,255,0.2)' }}
          >
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h4" gutterBottom color='white'>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={<Verified />}
                label={user?.role || 'Customer'}
                color="secondary"
                variant="filled"
              />
              <Chip
                label={user?.status || 'Active'}
                color="success"
                variant="outlined"
                sx={{ color: 'white', borderColor: 'white' }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Person />} label="Personal Info" />
          <Tab icon={<LocationOn />} label="Addresses" />
          <Tab icon={<ShoppingBag />} label="Order History" />
          <Tab icon={<Payment />} label="Payment Methods" />
          <Tab icon={<Notifications />} label="Preferences" />
          <Tab icon={<Security />} label="Security" />
        </Tabs>
      </Paper>

      {/* Personal Information Tab */}
      <TabPanel value={activeTab} index={0}>
        <Card>
          <CardHeader
            title="Personal Information"
            action={
              <Button
                startIcon={editMode ? <Save /> : <Edit />}
                onClick={editMode ? handleProfileUpdate : () => setEditMode(true)}
                variant={editMode ? 'contained' : 'outlined'}
              >
                {editMode ? 'Save Changes' : 'Edit Profile'}
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={profileData.phoneNumber}
                  onChange={(e) => setProfileData({ ...profileData, phoneNumber: e.target.value })}
                  disabled={!editMode}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  disabled={!editMode}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: <LocationOn sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid> */}
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {editMode && (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleProfileUpdate}
                      startIcon={<Save />}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setEditMode(false)}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                startIcon={<Delete />}
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Deleting...' : 'Delete Account'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Addresses Tab */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="primary" />
                <Typography variant="h6">Saved Addresses</Typography>
                <Chip 
                  label={`${(customerAddresses || []).length} address${(customerAddresses || []).length !== 1 ? 'es' : ''}`} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
            }
            action={
              <Button
                startIcon={<Add />}
                onClick={() => setAddressDialogOpen(true)}
                variant="contained"
                size="large"
                sx={{ borderRadius: 2 }}
              >
                Add New Address
              </Button>
            }
          />
          <CardContent>
            {(customerAddresses || []).length === 0 ? (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <LocationOn sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary">
                  No addresses saved yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add your first address to make checkout faster and easier
                </Typography>
                <Button
                  startIcon={<Add />}
                  onClick={() => setAddressDialogOpen(true)}
                  variant="contained"
                  size="large"
                >
                  Add Your First Address
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {customerAddresses.map((address) => (
                  <Grid item xs={12} md={6} key={address.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          height: '100%',
                          border: address.default ? '2px solid' : '1px solid',
                          borderColor: address.default ? 'primary.main' : 'divider',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      >
                        {address.default && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -1,
                              right: -1,
                              bgcolor: 'primary.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: '0 4px 0 8px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            DEFAULT
                          </Box>
                        )}
                        <CardContent sx={{ pb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                {address.addressName}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Chip 
                                  label={address.addressType?.toLowerCase()} 
                                  size="small" 
                                  color={address.addressType?.toLowerCase() === 'home' ? 'primary' : address.addressType?.toLowerCase() === 'work' ? 'secondary' : 'default'}
                                  icon={address.addressType?.toLowerCase() === 'home' ? <Home /> : <Business />}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditAddress(address)}
                                sx={{ 
                                  bgcolor: 'action.hover',
                                  '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteAddress(address.id)}
                                sx={{ 
                                  bgcolor: 'action.hover',
                                  '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {address.street}<br />
                            {address.landmark && (
                              <>
                                <Box component="span" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                                  {address.landmark}
                                </Box><br />
                              </>
                            )}
                            {address.city}, {address.state} {address.postalCode}<br />
                            {address.country}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            )}
            {customerError && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {customerError}
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Order History Tab */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardHeader title="Recent Orders" />
          <CardContent>
            {orders && orders.length > 0 ? (
              <List>
                {orders.slice(0, 5).map((order, index) => (
                  <React.Fragment key={order.id}>
                    <ListItem>
                      <ListItemIcon>
                        <ShoppingBag />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Order #${order.id}`}
                        secondary={`${order.items?.length || 0} items • ₹${order.totalAmount?.toLocaleString()} • ${new Date(order.orderDate).toLocaleDateString()}`}
                      />
                      <Chip
                        label={order.status}
                        color={order.status === 'DELIVERED' ? 'success' : order.status === 'CANCELLED' ? 'error' : 'primary'}
                        size="small"
                      />
                    </ListItem>
                    {index < orders.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Alert severity="info">
                No orders found. Start shopping to see your order history here.
              </Alert>
            )}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button variant="outlined" component={RouterLink} to="/orders">
                View All Orders
              </Button>
            </Box>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Payment Methods Tab */}
      <TabPanel value={activeTab} index={3}>
        <Card>
          <CardHeader title="Payment Methods" />
          <CardContent>
            <Alert severity="info">
              Payment method management will be available soon. You can add and manage your payment methods during checkout.
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Preferences Tab */}
      <TabPanel value={activeTab} index={4}>
        <Card>
          <CardHeader title="Notification Preferences" />
          <CardContent>
            <List>
              <ListItem>
                <ListItemText
                  primary="Email Notifications"
                  secondary="Receive order updates and important information via email"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.emailNotifications}
                      onChange={() => handlePreferenceChange('emailNotifications')}
                    />
                  }
                  label=""
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="SMS Notifications"
                  secondary="Receive order updates and delivery notifications via SMS"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.smsNotifications}
                      onChange={() => handlePreferenceChange('smsNotifications')}
                    />
                  }
                  label=""
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Marketing Emails"
                  secondary="Receive promotional offers and product recommendations"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.marketingEmails}
                      onChange={() => handlePreferenceChange('marketingEmails')}
                    />
                  }
                  label=""
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Order Updates"
                  secondary="Receive notifications about order status changes"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.orderUpdates}
                      onChange={() => handlePreferenceChange('orderUpdates')}
                    />
                  }
                  label=""
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Newsletter"
                  secondary="Receive our monthly newsletter with industry insights"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.newsletter}
                      onChange={() => handlePreferenceChange('newsletter')}
                    />
                  }
                  label=""
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Security Tab */}
      <TabPanel value={activeTab} index={5}>
        <Card>
          <CardHeader title="Security Settings" />
          <CardContent>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Security />
                </ListItemIcon>
                <ListItemText
                  primary="Change Password"
                  secondary="Update your account password"
                />
                <Button variant="outlined" size="small" onClick={openChangePwdDialog}>
                  Change
                </Button>
              </ListItem>
              {/* <Divider />
              <ListItem>
                <ListItemIcon>
                  <Phone />
                </ListItemIcon>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary="Add an extra layer of security to your account"
                />
                <Button variant="outlined" size="small">
                  Enable
                </Button>
              </ListItem> */}
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <History />
                </ListItemIcon>
                <ListItemText
                  primary="Login History"
                  secondary="View your recent login activity"
                />
                <Button variant="outlined" size="small">
                  View
                </Button>
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Change Password Dialog */}
      <Dialog
        open={changePwdOpen}
        onClose={closeChangePwdDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Change Password
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {/* Stepper */}
          <Stepper activeStep={changePwdStep} sx={{ mb: 3 }}>
            {['Enter Email', 'Verify OTP', 'Reset Password'].map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {/* Success state */}
          {changePwdDone ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} mb={1}>
                Password Changed Successfully!
              </Typography>
              <Typography color="text.secondary">
                Your account password has been updated.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Step 0: Email */}
              {changePwdStep === 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter your registered email address and we'll send you a 6-digit OTP.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={changePwdEmail}
                    onChange={(e) => { setChangePwdEmail(e.target.value); setChangePwdEmailError(''); }}
                    error={!!changePwdEmailError}
                    helperText={changePwdEmailError}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><Email color="action" /></InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {/* Step 1: OTP */}
              {changePwdStep === 1 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    A 6-digit OTP has been sent to <strong>{changePwdEmail}</strong>. It expires in 5 minutes.
                  </Alert>
                  <TextField
                    fullWidth
                    label="Enter OTP"
                    value={changePwdOtp}
                    onChange={(e) => { setChangePwdOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setChangePwdOtpError(''); }}
                    error={!!changePwdOtpError}
                    helperText={changePwdOtpError}
                    inputProps={{ maxLength: 6, style: { letterSpacing: 8, fontSize: 24, textAlign: 'center' } }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Didn't receive the OTP?{' '}
                      {changePwdCooldown > 0 ? (
                        <span>Resend in {changePwdCooldown}s</span>
                      ) : (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={handleChangePwdResendOtp}
                          disabled={changePwdLoading}
                          sx={{ cursor: 'pointer' }}
                        >
                          Resend OTP
                        </Link>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Step 2: New Password */}
              {changePwdStep === 2 && (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>OTP verified. Set your new password.</Alert>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={changePwdShowNew ? 'text' : 'password'}
                    value={changePwdNew}
                    onChange={(e) => { setChangePwdNew(e.target.value); setChangePwdNewError(''); }}
                    error={!!changePwdNewError}
                    helperText={changePwdNewError || 'Min 8 characters, one uppercase, one number'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setChangePwdShowNew(!changePwdShowNew)} edge="end">
                            {changePwdShowNew ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={changePwdShowConfirm ? 'text' : 'password'}
                    value={changePwdConfirm}
                    onChange={(e) => { setChangePwdConfirm(e.target.value); setChangePwdConfirmError(''); }}
                    error={!!changePwdConfirmError}
                    helperText={changePwdConfirmError}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setChangePwdShowConfirm(!changePwdShowConfirm)} edge="end">
                            {changePwdShowConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {changePwdDone ? (
            <Button variant="contained" onClick={closeChangePwdDialog} sx={{ background: 'linear-gradient(45deg, #2C3E50, #34495E)' }}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={closeChangePwdDialog} disabled={changePwdLoading}>
                Cancel
              </Button>
              {changePwdStep === 0 && (
                <Button
                  variant="contained"
                  onClick={handleChangePwdSendOtp}
                  disabled={changePwdLoading}
                  sx={{ background: 'linear-gradient(45deg, #2C3E50, #34495E)', '&:hover': { background: 'linear-gradient(45deg, #1A252F, #2C3E50)' } }}
                >
                  {changePwdLoading ? <CircularProgress size={20} color="inherit" /> : 'Send OTP'}
                </Button>
              )}
              {changePwdStep === 1 && (
                <Button
                  variant="contained"
                  onClick={handleChangePwdVerifyOtp}
                  disabled={changePwdLoading}
                  sx={{ background: 'linear-gradient(45deg, #2C3E50, #34495E)', '&:hover': { background: 'linear-gradient(45deg, #1A252F, #2C3E50)' } }}
                >
                  {changePwdLoading ? <CircularProgress size={20} color="inherit" /> : 'Verify OTP'}
                </Button>
              )}
              {changePwdStep === 2 && (
                <Button
                  variant="contained"
                  onClick={handleChangePwdReset}
                  disabled={changePwdLoading}
                  sx={{ background: 'linear-gradient(45deg, #2C3E50, #34495E)', '&:hover': { background: 'linear-gradient(45deg, #1A252F, #2C3E50)' } }}
                >
                  {changePwdLoading ? <CircularProgress size={20} color="inherit" /> : 'Change Password'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add/Edit Address Dialog */}
      <Dialog
        open={addressDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn color="primary" />
            <Typography variant="h6" component="span">
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {editingAddress ? 'Update your address details' : 'Add a new address to your profile'}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Address Type *</InputLabel>
                <Select
                  value={newAddress.type}
                  label="Address Type *"
                  onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
                  startAdornment={
                    newAddress.type === 'home' ? <Home sx={{ mr: 1, color: 'text.secondary' }} /> :
                    newAddress.type === 'work' ? <Business sx={{ mr: 1, color: 'text.secondary' }} /> :
                    null
                  }
                >
                  <MenuItem value="home">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Home fontSize="small" />
                      Home
                    </Box>
                  </MenuItem>
                  <MenuItem value="work">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business fontSize="small" />
                      Work
                    </Box>
                  </MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address Name"
                value={newAddress.name}
                onChange={(e) => handleAddressFieldChange('name', e.target.value)}
                placeholder="e.g., Home, Office, Mom's House"
                required
                error={!!addressErrors.name}
                helperText={addressErrors.name || "Give your address a memorable name"}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={newAddress.street}
                onChange={(e) => handleAddressFieldChange('street', e.target.value)}
                placeholder="Enter your complete street address"
                multiline
                rows={2}
                required
                error={!!addressErrors.street}
                helperText={addressErrors.street || "Include house/flat number, street name"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Landmark"
                value={newAddress.landmark}
                onChange={(e) => handleAddressFieldChange('landmark', e.target.value)}
                placeholder="Near landmark (optional)"
                helperText="This helps delivery partners find you easily"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={newAddress.city}
                onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                required
                error={!!addressErrors.city}
                helperText={addressErrors.city}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={newAddress.state}
                onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                required
                error={!!addressErrors.state}
                helperText={addressErrors.state}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={newAddress.pincode}
                onChange={(e) => handleAddressFieldChange('pincode', e.target.value)}
                required
                error={!!addressErrors.pincode}
                helperText={addressErrors.pincode || "6-digit postal code"}
                inputProps={{ pattern: '[0-9]{6}', maxLength: 6 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={newAddress.country}
                onChange={(e) => handleAddressFieldChange('country', e.target.value)}
                required
                error={!!addressErrors.country}
                helperText={addressErrors.country}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'action.hover', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAddress.isDefault}
                      onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Set as default address
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This address will be used as default for orders
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleDialogClose}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddressSubmit} 
            variant="contained"
            disabled={customerLoading}
            size="large"
            sx={{ minWidth: 120 }}
          >
            {customerLoading ? 'Saving...' : (editingAddress ? 'Update Address' : 'Add Address')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete your account? This action cannot be undone.
          </Typography>

          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            Please provide the OTP sent on your email to delete your account.
          </Typography>

          <TextField
            fullWidth
            label="Enter OTP"
            value={deletionOtp}
            onChange={(e) => {
              setDeletionOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
              setDeletionOtpError('');
            }}
            error={!!deletionOtpError}
            helperText={deletionOtpError}
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            disabled={deletingAccount || deletionOtpSending}
            size="small"
            sx={{ mb: 1 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            {deletionOtpSending ? (
              <Typography variant="caption" color="text.secondary">Sending OTP…</Typography>
            ) : deletionResendCooldown > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Resend OTP in {deletionResendCooldown}s
              </Typography>
            ) : (
              <Button
                size="small"
                variant="text"
                onClick={handleResendDeletionOtp}
                disabled={deletingAccount}
              >
                Resend OTP
              </Button>
            )}
          </Box>

          <Typography variant="body2">
            Please review our{' '}
            <Link
              component={RouterLink}
              to="/delete-account"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 500 }}
            >
              Account Deletion Terms
            </Link>{' '}
            before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} variant="outlined" disabled={deletingAccount}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteAccount}
            variant="contained"
            color="error"
            disabled={deletingAccount || deletionOtpSending}
          >
            {deletingAccount ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;